import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Development bypass - disabled for normal operation
  const DEVELOPMENT_BYPASS = false;

  useEffect(() => {
    console.log('AuthProvider: useEffect triggered');
    console.log('AuthProvider: Environment check:', {
      origin: window.location.origin,
      pathname: window.location.pathname,
      developmentBypass: DEVELOPMENT_BYPASS
    });
    
    // Development bypass mode
    if (DEVELOPMENT_BYPASS) {
      console.log('Development bypass is active');
      setUser({ id: 'dev-user', email: 'dev@admin.com' } as User);
      setSession({ user: { id: 'dev-user', email: 'dev@admin.com' } } as Session);
      setIsAdmin(true);
      setIsLoading(false);
      return;
    }

    console.log('AuthProvider: Setting up auth state listener');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('AuthProvider: Auth state changed:', { event, session, user: session?.user });
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Check if user is admin
          setTimeout(async () => {
            try {
              console.log('AuthProvider: Checking admin role for user:', session.user.id);
              const { data, error } = await supabase
                .from('user_roles')
                .select('role')
                .eq('user_id', session.user.id)
                .eq('role', 'admin')
                .single();
              
              console.log('AuthProvider: Admin check result:', { data, error });
              setIsAdmin(!!data && !error);
            } catch (error) {
              console.log('AuthProvider: Admin check error:', error);
              setIsAdmin(false);
            }
          }, 0);
        } else {
          console.log('AuthProvider: No user session, setting isAdmin to false');
          setIsAdmin(false);
        }
        
        setIsLoading(false);
      }
    );

    // Check for existing session
    console.log('AuthProvider: Checking for existing session');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('AuthProvider: Got existing session:', session);
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [DEVELOPMENT_BYPASS]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const redirectUrl = `${window.location.origin}/admin-portal`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });
      return { error };
    } catch (error) {
      return { error };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to sign out",
        variant: "destructive",
      });
    }
  };


  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        session, 
        isAdmin, 
        isLoading, 
        signIn, 
        signUp, 
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};